import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import VendorType from "../payments/types/Vendor";

export default function Vendor({ vendor, area }: { vendor: VendorType; area: string }) {
  const [accordionState, setAccordionState] = useState<{ [key: string]: boolean }>({});
  const accordionContentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const accordionBodyRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const vendorLocation: any =
    vendor.locations.length > 1
      ? vendor.locations.find((location) => location.neighbourhood === area) || area
      : vendor.locations[0];

  useEffect(() => {
    for (const vendorId in accordionState) {
      const isOpen = accordionState[vendorId];
      const accordionBody = accordionBodyRefs.current[vendorId];
      const accordionContent = accordionContentRefs.current[vendorId];

      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (accordionContent && accordionBody) {
        if (isOpen) {
          gsap.set(accordionContent, { height: "auto" });
          gsap.from(accordionContent, { duration: reduceMotion ? 0 : 0.25, height: 0 });
          accordionContent.removeAttribute("hidden");
          accordionBody.removeAttribute("hidden");
        } else {
          gsap.to(accordionContent, { duration: reduceMotion ? 0 : 0.25, height: 0 }).then(() => {
            accordionContent.setAttribute("hidden", "true");
            accordionBody.setAttribute("hidden", "true");
          });
        }
      }
    }
  }, [accordionState]);

  const handleAccordionClick = (vendorId: number) => {
    setAccordionState((prevState) => ({
      ...prevState,
      [vendorId]: prevState[vendorId] === undefined ? true : !prevState[vendorId],
    }));
  };

  return (
    <div className="vendor accordion" key={vendor.id}>
      <h3 className="accordion-header font-family-lato font-size-m">
        <button
          className="accordion-toggle bold"
          aria-controls={`vendor-${vendor.id}`}
          aria-expanded={accordionState[vendor.id] ? "true" : "false"}
          onClick={() => handleAccordionClick(vendor.id)}
          id={`vendor-${vendor.id}-heading`}
        >
          {vendor.name}
          {vendorLocation.name && ` at ${vendorLocation.name}`}
        </button>
      </h3>
      <div
        hidden
        className="accordion-body"
        aria-hidden={!accordionState[vendor.id]}
        ref={(el) => (accordionBodyRefs.current[vendor.id] = el)}
      >
        <div
          hidden
          id={`vendor-${vendor.id}`}
          className="accordion-body-content vendor-info"
          aria-hidden={!accordionState[vendor.id]}
          aria-labelledby={`vendor-${vendor.id}-heading`}
          role="region"
          ref={(el) => (accordionContentRefs.current[vendor.id] = el)}
        >
          <div className="vendor-detail">
            {vendor.image.cover.url ? (
              <img className="vendor-photo" alt="" src={vendor.image.cover.url} />
            ) : (
              <div className="vendor-photo placeholder"></div>
            )}
            <div className="vendor-actions">
              <a href={`/?badge=${vendor.badge_id}`} className="button font-size-s">
                Pay Vendor
              </a>
              {vendorLocation.name || vendorLocation.cross_street ? (
                <a
                  className="button button--outline font-size-s"
                  href={`https://www.google.com/maps/search/?api=1&query=${
                    vendorLocation.cross_street
                  } ${vendorLocation.name ? vendorLocation.name : ""}`}
                  target="_blank"
                >
                  Open in Google Maps
                </a>
              ) : null}
            </div>
          </div>
          {vendorLocation.description || vendorLocation.hours !== "<p></p>" ? (
            <div className="vendor-location">
              <h4 className="font-size-m bold">
                {vendorLocation.name}{" "}
                {vendorLocation.cross_street && `(${vendorLocation.cross_street})`}
              </h4>
              <p className="font-size-s">{vendorLocation.description}</p>
              {vendorLocation.hours !== "<p></p>" && (
                <p className="font-size-s">
                  <strong>Usual hours</strong>:<br />
                  <span
                    dangerouslySetInnerHTML={{
                      __html: vendorLocation.hours.replace(/<p>/g, "").replace(/<\/p>/g, "<br>"),
                    }}
                  />
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
